import React, { createContext, useContext, useState } from 'react';
import { Select, Tag } from 'antd';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const SortableTagWrapper = styled.div<{ 
  isDragging?: boolean; 
  isOver?: boolean;
  showPlaceholderBefore?: boolean;
  showPlaceholderAfter?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  margin: 2px 4px 2px 0;
  position: relative;
  
  ${(props) => props.isDragging && `
    opacity: 0;
    pointer-events: none;
  `}
  
  ${(props) => props.isOver && !props.isDragging && !props.showPlaceholderBefore && !props.showPlaceholderAfter && `
    &::before {
      content: '';
      position: absolute;
      inset: -2px;
      border: 1px dashed #4096ff;
      border-radius: 4px;
      background-color: rgba(64, 150, 255, 0.04);
      pointer-events: none;
      z-index: -1;
    }
  `}

  ${(props) => props.showPlaceholderBefore && `
    &::before {
      content: '';
      position: absolute;
      left: -8px;
      top: 0;
      bottom: 0;
      width: 3px;
      background: linear-gradient(to right, #4096ff, rgba(64, 150, 255, 0.3));
      border-radius: 2px;
      pointer-events: none;
      z-index: 10;
      animation: pulse 1.5s ease-in-out infinite;
      box-shadow: 0 0 4px rgba(64, 150, 255, 0.6);
    }
  `}

  ${(props) => props.showPlaceholderAfter && `
    &::after {
      content: '';
      position: absolute;
      right: -8px;
      top: 0;
      bottom: 0;
      width: 3px;
      background: linear-gradient(to left, #4096ff, rgba(64, 150, 255, 0.3));
      border-radius: 2px;
      pointer-events: none;
      z-index: 10;
      animation: pulse 1.5s ease-in-out infinite;
      box-shadow: 0 0 4px rgba(64, 150, 255, 0.6);
    }
  `}

  @keyframes pulse {
    0%, 100% {
      opacity: 0.4;
    }
    50% {
      opacity: 1;
    }
  }
`;

const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 4px;
  color: #8c8c8c;
  cursor: grab;
  font-size: 12px;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    color: #4096ff;
  }
`;

const CloseButton = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  transition: color 0.2s;
  flex-shrink: 0;

  &:hover {
    color: rgba(0, 0, 0, 0.85);
  }
`;


interface SortableContextType {
  tags: string[];
  activeId: string | null;
  overId: string | null;
  isDragging: boolean;
}

const SortableTagsContext = createContext<SortableContextType | null>(null);

interface SortableTagProps {
  tag: string;
  label: React.ReactNode;
  closable: boolean;
  onClose: (e?: React.MouseEvent<HTMLElement>) => void;
}

function SortableTagItem({ tag, label, closable, onClose }: SortableTagProps) {
  const context = useContext(SortableTagsContext);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tag });

  if (!context) {
    // Fallback если контекст недоступен
    return <Tag closable={closable} onClose={onClose}>{label}</Tag>;
  }

  const style = {
    transform: (isDragging || context.isDragging) ? 'none' : CSS.Transform.toString(transform),
    transition: (isDragging || context.isDragging) ? 'none' : transition,
  };

  const handleCloseClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onClose?.(e);
  };

  const isOver = Boolean(
    context.activeId && 
    context.activeId !== tag && 
    context.overId === tag && 
    !isDragging
  );

  // Определяем, показывать ли placeholder до или после элемента
  // arrayMove работает так: arrayMove([a, b, c, d], oldIndex, newIndex)
  // Элемент всегда встает на позицию newIndex
  // Если oldIndex > newIndex: элемент встанет ПЕРЕД элементом, который был на позиции newIndex
  // Если oldIndex < newIndex: элемент встанет ПОСЛЕ элемента, который был на позиции newIndex
  const currentIndex = context.tags.indexOf(tag);
  const activeIndex = context.activeId ? context.tags.indexOf(context.activeId) : -1;
  
  // Если activeIndex > currentIndex: перетаскиваем вниз, элемент встанет ПЕРЕД текущим (индикатор слева)
  // Если activeIndex < currentIndex: перетаскиваем вверх, элемент встанет ПОСЛЕ текущего (индикатор справа)
  // Но нужно учесть, что после удаления элемента из старой позиции индексы сдвигаются
  // На самом деле, arrayMove([a, b, c, d], 1, 3) даст [a, c, d, b] - b встал ПОСЛЕ d
  // arrayMove([a, b, c, d], 3, 1) даст [a, d, b, c] - d встал ПЕРЕД b
  // Поэтому: если activeIndex > currentIndex, показываем слева (элемент встанет ПЕРЕД)
  // Если activeIndex < currentIndex, показываем справа (элемент встанет ПОСЛЕ)
  const showPlaceholderBefore = isOver && activeIndex !== -1 && currentIndex !== -1 && activeIndex > currentIndex;
  const showPlaceholderAfter = isOver && activeIndex !== -1 && currentIndex !== -1 && activeIndex < currentIndex;

  return (
    <SortableTagWrapper 
      ref={setNodeRef} 
      style={style} 
      isDragging={isDragging} 
      isOver={isOver}
      showPlaceholderBefore={showPlaceholderBefore}
      showPlaceholderAfter={showPlaceholderAfter}
    >
      <Tag>
        <DragHandle {...listeners} {...attributes}>
          {label}
        </DragHandle>
        {closable && (
          <CloseButton onClick={handleCloseClick}>
            <CloseOutlined />
          </CloseButton>
        )}
      </Tag>
    </SortableTagWrapper>
  );
}

interface SortableTagsInputProps {
  options?: Array<{ label: string; value: string }>;
  placeholder?: string;
  showSearch?: boolean;
  optionFilterProp?: string;
  [key: string]: any;
}

const SortableTagsInput: React.FC<SortableTagsInputProps> = ({
  options = [],
  placeholder = 'Tags',
  showSearch = true,
  optionFilterProp = 'label',
  value = [],
  onChange,
  ...rest
}) => {
  const tags = value || [];
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [activeTag, setActiveTag] = useState<{ label: React.ReactNode; value: string } | null>(null);
  const tagDataMap = React.useRef<Map<string, { label: React.ReactNode; value: string }>>(new Map());

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    setActiveId(id);
    // Находим информацию о перетаскиваемом теге из сохраненных данных
    const tagData = tagDataMap.current.get(id);
    if (tagData) {
      setActiveTag(tagData);
    } else {
      // Fallback: используем сам id как label
      setActiveTag({ label: id, value: id });
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverId(event.over?.id as string || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && tags.length > 0) {
      const oldIndex = tags.indexOf(active.id as string);
      const newIndex = tags.indexOf(over.id as string);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newTags = arrayMove(tags, oldIndex, newIndex);
        onChange?.(newTags);
      }
    }
    
    setActiveId(null);
    setOverId(null);
    setActiveTag(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
    setActiveTag(null);
  };

  const handleChange = (newTags: string[]) => {
    onChange?.(newTags);
  };

  const tagRender = (props: any) => {
    const { label, value: tagValue, closable, onClose } = props;
    // Сохраняем информацию о теге для DragOverlay
    tagDataMap.current.set(tagValue, { label, value: tagValue });
    return (
      <SortableTagItem
        key={tagValue}
        tag={tagValue}
        label={label}
        closable={closable}
        onClose={onClose}
      />
    );
  };

  const renderDragOverlay = () => {
    if (!activeTag) return null;
    
    return (
      <SortableTagWrapper isDragging={false}>
        <Tag>
          <DragHandle>
            {activeTag.label}
          </DragHandle>
        </Tag>
      </SortableTagWrapper>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={tags}
        strategy={rectSortingStrategy}
      >
        <SortableTagsContext.Provider value={{ tags, activeId, overId, isDragging: !!activeId }}>
          <Select
            {...rest}
            mode="tags"
            tokenSeparators={[',']}
            showSearch={showSearch}
            optionFilterProp={optionFilterProp}
            placeholder={placeholder}
            options={options}
            tagRender={tagRender}
            value={tags}
            onChange={handleChange}
          />
        </SortableTagsContext.Provider>
      </SortableContext>
      <DragOverlay>
        {renderDragOverlay()}
      </DragOverlay>
    </DndContext>
  );
};

export default SortableTagsInput;
